// Observability — send this organisation's run traces to its own LangSmith.
//
// Every governed run (Playground, Chat, and the runtime API) becomes one trace:
// the LLM calls, tool calls and validator decisions in order, with tokens and
// timings. Traces contain prompts and answers, which is why the destination is
// the organisation's own LangSmith account and why only org admins set it.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, CheckCircle2, XCircle } from "lucide-react";
import * as React from "react";

import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
} from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import {
  deleteObservability,
  getObservability,
  putObservability,
  testObservability,
} from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";
import type { ObservabilitySettings, ObservabilityTestResult } from "@/types/api";

export default function ObservabilityPage() {
  const q = useQuery({ queryKey: qk.observability, queryFn: getObservability });
  return (
    <div>
      <PageHeader
        title="Observability"
        description="Trace every agent run to your organisation's own LangSmith project."
      />
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.isLoading || !q.data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <LangSmithCard current={q.data} />
      )}
    </div>
  );
}

function LangSmithCard({ current }: { current: ObservabilitySettings }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = React.useState(current.enabled);
  const [project, setProject] = React.useState(current.project);
  const [endpoint, setEndpoint] = React.useState(current.endpoint);
  const [apiKey, setApiKey] = React.useState("");
  const [test, setTest] = React.useState<ObservabilityTestResult | null>(null);
  const [confirmRemove, setConfirmRemove] = React.useState(false);

  React.useEffect(() => {
    setEnabled(current.enabled);
    setProject(current.project);
    setEndpoint(current.endpoint);
  }, [current]);

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.observability });

  const save = useMutation({
    mutationFn: () =>
      putObservability({
        enabled,
        project: project.trim(),
        endpoint: endpoint.trim() || undefined,
        api_key: apiKey.trim() || undefined,
      }),
    onSuccess: async () => {
      setApiKey("");
      setTest(null);
      await invalidate();
      toast.success(enabled ? "Tracing is on." : "Settings saved.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const check = useMutation({
    mutationFn: testObservability,
    onSuccess: setTest,
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: deleteObservability,
    onSuccess: async () => {
      setConfirmRemove(false);
      setTest(null);
      await invalidate();
      toast.success("LangSmith disconnected.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <Activity className="h-4 w-4 text-primary" />
        <CardTitle className="text-base">LangSmith</CardTitle>
        {current.enabled ? (
          <Badge variant="success">Tracing</Badge>
        ) : current.configured ? (
          <Badge variant="secondary">Paused</Badge>
        ) : (
          <Badge variant="secondary">Not connected</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Create an API key in LangSmith (Settings → API Keys). Runs are traced with the Aviary run
          id as the trace id, so a trace can be found from any row in Audit logs.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="ls-key">API key</Label>
            <Input
              id="ls-key"
              type="password"
              autoComplete="off"
              placeholder={
                current.key_hint ? `stored (${current.key_hint}) — enter to replace` : "lsv2_…"
              }
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ls-project">Project</Label>
            <Input id="ls-project" value={project} onChange={(e) => setProject(e.target.value)} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="ls-endpoint">Endpoint</Label>
            <Input
              id="ls-endpoint"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Change only for the EU region or a self-hosted LangSmith.
            </p>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input accent-primary"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Trace every run
        </label>

        {test ? (
          <p
            className={`flex items-center gap-2 text-sm ${test.ok ? "text-success" : "text-destructive"}`}
          >
            {test.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {test.detail}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          {current.configured ? (
            <>
              <Button variant="outline" onClick={() => setConfirmRemove(true)}>
                Disconnect
              </Button>
              <Button variant="outline" disabled={check.isPending} onClick={() => check.mutate()}>
                {check.isPending ? "Testing…" : "Test connection"}
              </Button>
            </>
          ) : null}
          <Button disabled={save.isPending || !project.trim()} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title="Disconnect LangSmith?"
        description="The stored API key is deleted and runs stop being traced. Existing traces stay in LangSmith."
        confirmLabel="Disconnect"
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </Card>
  );
}
